import type {
  ICountriesAPIResponse,
  ICountry,
  IStatesAPIResponse,
  OptionType,
} from "../types/types";
import { useContext, useState } from "react";
import useSWR from "swr";
import { Notifications } from "../components/Notifications/NotificationsContext";
import { useStoreState } from "./util";
import { endpoints } from "../endpoints";
import type {
  ListDetailsSchema,
  ListItemObjectSchema,
} from "../types/types.PIM";

export const useCountriesList = (): OptionType<string>[] => {
  const [countries, setCountries] = useState<OptionType<string>[]>([]);
  const { notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  useSWR<ICountriesAPIResponse>(
    `/v1/storefronts/${storefront_id}/location/countries`,
    {
      onError: (error) => {
        notifyError("There was an error fetching the countries list.", {
          error,
        });
      },
      refreshInterval: (() => {
        if (!countries.length) return 1000;
        return 0;
      })(),
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
      onSuccess: (data) => {
        setCountries(
          data.map((country: ICountry) => {
            return { value: country.alpha_2, label: country.name };
          })
        );
      },
    }
  );
  return countries;
};

export const useStatesList = (
  selectedCountry: string | undefined | null
): OptionType[] => {
  const [states, setStates] = useState<OptionType[]>([]);
  const { notifyError } = useContext(Notifications);

  useSWR<IStatesAPIResponse>(
    selectedCountry ? `/v1/location/countries/${selectedCountry}/states` : null,
    {
      onError: (error) => {
        notifyError("There was an error fetching the states list.", { error });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
      onSuccess: (data) => {
        setStates(
          data.map((state) => {
            return { value: state.code.split("-").pop(), label: state.name };
          })
        );
      },
    }
  );
  return states;
};

export const useRegionsList = (): OptionType<string>[] => {
  //setRegions
  const [regions, setRegions] = useState<OptionType<string>[]>([]);
  const { notifyError } = useContext(Notifications);
  const { tenant_id } = useStoreState();
  useSWR<{ data: ListDetailsSchema }>(
    endpoints.v2_tenants_id_pim_get_list_by_name(tenant_id, "Regions"),
    {
      onError: (error) => {
        notifyError("There was an error fetching the regions list.", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
      onSuccess: (data) => {
        setRegions(
          data.data.items.map((region: ListItemObjectSchema) => {
            return { value: region.name, label: region.name };
          })
        );
      },
    }
  );
  return regions;
};
